import { useMemo, useState } from 'react'

import { DonutChart } from '@mantine/charts'
import {
  Alert,
  Box,
  Checkbox,
  Group,
  Paper,
  SegmentedControl,
  Skeleton,
  Stack,
  Text,
  Title,
} from '@mantine/core'

import { usdFormatter } from '@repo/common/helpers/formatters'
import { useAdminFund } from '@repo/common/queries/admin/fund/fund_queries'
import {
  type FundCompositionItem,
  useFundComposition,
} from '@repo/common/queries/admin/fund/query_fund_composition'

import { TowerCard } from '@/components/ui/TowerCard'

import classes from './FundCompositionChart.module.css'

// todo use a lib for these
const COLORS = [
  '#0088FE', // blue
  '#00C49F', // teal
  '#FFBB28', // yellow
  '#FF8042', // orange
  '#A28FD0', // purple
  '#FF6B6B', // red
  '#54A0FF', // light blue
  '#5D9CEC', // periwinkle
  '#4BC0C0', // turquoise
  '#FFA726', // amber
  '#66BB6A', // green
  '#EC407A', // pink
  '#7E57C2', // deep purple
  '#78909C', // blue grey
  '#26A69A', // teal accent
  '#EF5350', // red accent
]

// Types for grouping options
type GroupingOptions = {
  byShareClass: boolean
  bySpv: boolean
  bySpvShareClass: boolean
}

// Type for the grouped data
type GroupedDataItem = {
  name: string
  value: number
  items: FundCompositionItem[]
  color?: string
  isCash?: boolean
}

interface FundCompositionChartProps {
  fundId: string
  date?: string
  executedAt?: string
  height?: number
  width?: string | number
}

export function FundCompositionChart({
  fundId,
  executedAt,
  height = 400,
  // width = '100%',
}: FundCompositionChartProps) {
  const [displayMode, setDisplayMode] = useState<'chart' | 'table'>('chart')
  const [groupingOptions, setGroupingOptions] = useState<GroupingOptions>({
    byShareClass: false,
    bySpv: false,
    bySpvShareClass: false,
  })

  const {
    data: fundCompositionData,
    isLoading: isLoadingComposition,
    error: compositionError,
  } = useFundComposition(fundId, executedAt)

  const {
    data: fundData,
    isLoading: isLoadingFund,
    error: fundError,
  } = useAdminFund(fundId)

  const isLoading = isLoadingComposition || isLoadingFund
  const error = compositionError || fundError

  const groupedData = useMemo(() => {
    if (!fundCompositionData?.items.length && !fundData) return []

    const groupMap = new Map<string, GroupedDataItem>()

    // Process regular fund composition items
    if (fundCompositionData?.items.length) {
      fundCompositionData.items.forEach((item) => {
        let groupKey = item.asset_name

        if (groupingOptions.byShareClass) {
          groupKey += ` - ${item.share_class}`
        }

        if (groupingOptions.bySpv && item.spv) {
          groupKey += ` - ${item.spv}`

          if (groupingOptions.bySpvShareClass && item.spv_share_class) {
            groupKey += ` - ${item.spv_share_class}`
          }
        }

        const existingGroup = groupMap.get(groupKey)
        if (existingGroup) {
          existingGroup.value += item.total_cost
          existingGroup.items.push(item)
        } else {
          groupMap.set(groupKey, {
            name: groupKey,
            value: item.total_cost,
            items: [item],
          })
        }
      })
    }

    // Add cash as a synthetic item if available
    const cashValue = fundData?.live_nav?.cash_total_value
    if (cashValue && cashValue > 0) {
      const cashItem: FundCompositionItem = {
        asset_id: 'cash',
        asset_name: 'Cash',
        asset_category: 'cash',
        share_class: '',
        shares_held: 0,
        spv: null,
        spv_share_class: null,
        total_cost: cashValue,
      }

      groupMap.set('Cash', {
        name: 'Cash',
        value: cashValue,
        items: [cashItem],
        isCash: true,
      })
    }

    return Array.from(groupMap.values())
      .sort((a, b) => b.value - a.value)
      .map((item, index) => ({
        ...item,
        color: COLORS[index % COLORS.length]!,
      }))
  }, [fundCompositionData, fundData, groupingOptions])

  const totalHoldings = useMemo(() => {
    if (!groupedData.length) return 0
    return groupedData.reduce((sum, item) => sum + item.value, 0)
  }, [groupedData])

  const donutChartData = useMemo(() => {
    return groupedData.map((item) => ({
      name: item.name,
      value: item.value,
      color: item.color,
    }))
  }, [groupedData])

  const separatedLegendData = useMemo(() => {
    if (!groupedData.length)
      return { companies: [], externalFunds: [], cash: [] }

    const companies = groupedData.filter(
      (item) => item.items[0]!.asset_category === 'company',
    )
    const externalFunds = groupedData.filter(
      (item) => item.items[0]!.asset_category === 'external_fund',
    )
    const cash = groupedData.filter((item) => item.isCash)

    return { companies, externalFunds, cash }
  }, [groupedData])

  const formatTooltipValue = (value: number) => {
    return value.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
  }

  const toggleGrouping = (option: keyof GroupingOptions) => {
    setGroupingOptions((prev) => ({
      ...prev,
      [option]: !prev[option],
    }))
  }

  if (isLoading) {
    return (
      <TowerCard title="Fund Composition" description="Loading...">
        <Box className={classes.loading_container}>
          <Skeleton height={height} width="100%" radius="md" />
        </Box>
      </TowerCard>
    )
  }

  if (error || !fundCompositionData) {
    // todo consistency
    return (
      <Paper
        shadow="md"
        p="lg"
        radius="md"
        withBorder
        className={classes.container}
      >
        <Title order={3}>Holdings Composition</Title>
        <Alert color="red" title="Error Loading Data">
          Unable to load composition data for the specified fund and date.
        </Alert>
      </Paper>
    )
  }

  return (
    <TowerCard
      title="Holdings Composition"
      description={`Fund composition breakdown${executedAt ? ' as of ' + executedAt : ' (latest)'} `}
    >
      <Box className={classes.chart_controls}>
        <Group justify="space-between">
          <SegmentedControl
            value={displayMode}
            onChange={(value) => {
              setDisplayMode(value as 'chart' | 'table')
            }}
            data={[
              { label: 'Chart', value: 'chart' },
              { label: 'Table', value: 'table' },
            ]}
          />
          <Text size="sm" c="dimmed">
            Total Holdings: {usdFormatter(totalHoldings)}
          </Text>
        </Group>
      </Box>

      <Box className={classes.grouping_controls}>
        <Group>
          <Text size="sm" fw={500}>
            Group by:
          </Text>
          <Checkbox
            label="Share Class"
            checked={groupingOptions.byShareClass}
            onChange={() => {
              toggleGrouping('byShareClass')
            }}
          />
          <Checkbox
            label="SPV"
            checked={groupingOptions.bySpv}
            onChange={() => {
              toggleGrouping('bySpv')
            }}
          />
          <Checkbox
            label="SPV Share Class"
            checked={groupingOptions.bySpvShareClass}
            disabled={!groupingOptions.bySpv}
            onChange={() => {
              toggleGrouping('bySpvShareClass')
            }}
          />
        </Group>
      </Box>

      {displayMode === 'chart' ? (
        <Box className={classes.chart_container} style={{ height }}>
          <DonutChart
            data={donutChartData}
            size={height - 160}
            thickness={60}
            paddingAngle={0}
            withTooltip
            tooltipDataSource="segment"
            withLabels
            labelsType="percent"
            valueFormatter={formatTooltipValue}
          />

          <Stack gap="md" mt="md" ml="md">
            {separatedLegendData.companies.length > 0 && (
              <Box>
                <Text size="sm" fw={600} mb="xs">
                  Companies
                </Text>
                <Group gap="md">
                  {separatedLegendData.companies.map((item) => (
                    <Group key={item.name} gap="xs">
                      <Box
                        style={{
                          width: 12,
                          height: 12,
                          backgroundColor: item.color,
                          borderRadius: 2,
                        }}
                      />
                      <Text size="xs" c="dimmed">
                        {item.name}
                      </Text>
                    </Group>
                  ))}
                </Group>
              </Box>
            )}

            {separatedLegendData.externalFunds.length > 0 && (
              <Box>
                <Text size="sm" fw={600} mb="xs">
                  External Funds
                </Text>
                <Group gap="md">
                  {separatedLegendData.externalFunds.map((item) => (
                    <Group key={item.name} gap="xs">
                      <Box
                        style={{
                          width: 12,
                          height: 12,
                          backgroundColor: item.color,
                          borderRadius: 2,
                        }}
                      />
                      <Text size="xs" c="dimmed">
                        {item.name}
                      </Text>
                    </Group>
                  ))}
                </Group>
              </Box>
            )}

            {separatedLegendData.cash.length > 0 && (
              <Box>
                <Text size="sm" fw={600} mb="xs">
                  Cash
                </Text>
                <Group gap="md">
                  {separatedLegendData.cash.map((item) => (
                    <Group key={item.name} gap="xs">
                      <Box
                        style={{
                          width: 12,
                          height: 12,
                          backgroundColor: item.color,
                          borderRadius: 2,
                        }}
                      />
                      <Text size="xs" c="dimmed">
                        {item.name}
                      </Text>
                    </Group>
                  ))}
                </Group>
              </Box>
            )}
          </Stack>
        </Box>
      ) : (
        <Box className={classes.table_container}>
          <div className={classes.table_scroll_container}>
            <table className={classes.data_table}>
              <thead>
                <tr>
                  <th className={classes.company_name}>Holdings</th>
                  <th>Value</th>
                  <th>% of Total</th>
                  <th>Shares Held</th>
                  {groupingOptions.byShareClass && <th>Share Class</th>}
                  {groupingOptions.bySpv && <th>SPV</th>}
                  {groupingOptions.bySpvShareClass && groupingOptions.bySpv && (
                    <th>SPV Share Class</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {groupedData.map((group, index) => (
                  <tr key={`row-${index}`}>
                    <td
                      className={classes.company_name}
                      style={{ borderLeft: `4px solid ${group.color}` }}
                    >
                      {group.name}
                    </td>
                    <td>{usdFormatter(group.value)}</td>
                    <td>{((group.value / totalHoldings) * 100).toFixed(2)}%</td>
                    <td>
                      {group.isCash
                        ? '-'
                        : group.items
                            .reduce((sum, item) => sum + item.shares_held, 0)
                            .toLocaleString()}
                    </td>
                    {groupingOptions.byShareClass && (
                      <td>
                        {!group.items[0]!.share_class
                          ? '-'
                          : group.items[0]!.share_class}
                      </td>
                    )}
                    {groupingOptions.bySpv && (
                      <td>
                        {!group.items[0]!.spv ? '-' : group.items[0]!.spv}
                      </td>
                    )}
                    {groupingOptions.bySpvShareClass &&
                      groupingOptions.bySpv && (
                        <td>
                          {!group.items[0]!.spv_share_class
                            ? '-'
                            : group.items[0]!.spv_share_class}
                        </td>
                      )}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ fontWeight: 'bold' }}>
                  <td className={classes.company_name}>Total</td>
                  <td>{usdFormatter(totalHoldings)}</td>
                  <td>100.00%</td>
                  <td>
                    {fundCompositionData.items
                      .reduce((sum, item) => sum + item.shares_held, 0)
                      .toLocaleString()}
                  </td>
                  {groupingOptions.byShareClass && <td></td>}
                  {groupingOptions.bySpv && <td></td>}
                  {groupingOptions.bySpvShareClass && groupingOptions.bySpv && (
                    <td></td>
                  )}
                </tr>
              </tfoot>
            </table>
          </div>
        </Box>
      )}
    </TowerCard>
  )
}
